import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereLabel } from "@components/common/sphere-label";
import { useAuthContext } from "@context-providers/auth/auth-context";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Dialog, Link, PasswordValidationChecker, TextField, useToast } from "@flat-ui";
import { Grid, Stack } from "@mui-wrapper";
import { isValidCurrentPassword, isValidRepeatNewPassword } from "@pages/account-settings/sections/account-change-password/change-password-utils";
import { PasswordInputButton } from "@pages/account-settings/sections/account-change-password/password-input-button";
import { DEFAULT_BOLD_TEXT_FONT_WEIGHT, DEFAULT_INPUT_FONT_SIZE, withEllipsis } from "@styles/common-styles";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useMemo, useState } from "react";

/** Message from the backend when the current password is incorrect */
const INCORRECT_PASSWORD_MSG = "Username/password do not match.";

/** Possible error messages */
var ErrorMessages = /*#__PURE__*/function (ErrorMessages) {
  ErrorMessages["currentPassword"] = "Password is incorrect";
  ErrorMessages["newPassword2"] = "Input does not match new password";
  return ErrorMessages;
}(ErrorMessages || {});
/** Renders the change password button and dialog */
export function AccountChangePassword({
  email
}) {
  const {
    openToast
  } = useToast();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    logout
  } = useAuthContext();
  const {
    trackEvent
  } = useTrackEvent();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isNewPassword2Visible, setIsNewPassword2Visible] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [isCurrentPasswordError, setIsCurrentPasswordError] = useState(false);
  const [isNewPassword2Error, setIsNewPassword2Error] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const confirmText = useMemo(() => {
    if (isResetPassword) {
      return isResetSuccess ? "Sign out" : "Reset password";
    } else {
      return "Change password";
    }
  }, [isResetPassword, isResetSuccess]);

  // Handles form validation
  useEffect(() => {
    const isValid = isValidCurrentPassword(currentPassword) && isValidRepeatNewPassword(newPassword2, newPassword) && isPasswordValid;
    setIsValidForm(isValid);
  }, [currentPassword, isPasswordValid, newPassword, newPassword2]);

  // Handles error in `current password` field
  useEffect(() => {
    // Reset error if user deletes input
    if (!currentPassword) {
      return setIsCurrentPasswordError(false);
    }
    setIsCurrentPasswordError(!isValidCurrentPassword(currentPassword));
  }, [currentPassword]);

  // Handles error in `repeat new password` field
  useEffect(() => {
    if (newPassword2.length >= newPassword.length) {
      setIsNewPassword2Error(!isValidRepeatNewPassword(newPassword2, newPassword));
    }
  }, [newPassword, newPassword2]);
  function resetState() {
    setIsDialogOpen(true);
    setIsResetPassword(false);
    setIsLoading(false);
    setIsResetSuccess(false);
    setIsCurrentPasswordVisible(false);
    setIsNewPasswordVisible(false);
    setIsNewPassword2Visible(false);
    setCurrentPassword("");
    setNewPassword("");
    setNewPassword2("");
    setIsCurrentPasswordError(false);
    setIsNewPassword2Error(false);
    setIsValidForm(false);
  }
  function onOpen() {
    resetState();
  }
  function onForgotPassword() {
    setIsResetPassword(true);
  }
  async function resetPassword() {
    if (!email) {
      return;
    }
    trackEvent({
      name: UserProfile.updateProfile,
      props: {
        attribute: "reset password"
      }
    });
    setIsLoading(true);
    try {
      await faroCoreApiClient.users.resetUserPassword({
        // eslint-disable-next-line @typescript-eslint/naming-convention -- naming defined by backend
        resetpw_email: email
      });
      setIsResetSuccess(true);
      openToast({
        title: "Password reset email sent"
      });
    } catch (error) {
      handleErrorWithToast({
        id: `resetUserPassword-${Date.now().toString()}`,
        title: "Could not reset password. Please try again",
        error
      });
    }
    setIsLoading(false);
  }
  async function changePassword() {
    setIsLoading(true);
    trackEvent({
      name: UserProfile.updateProfile,
      props: {
        attribute: "change password"
      }
    });
    try {
      await faroCoreApiClient.users.setNewPassword({
        /* eslint-disable @typescript-eslint/naming-convention -- naming defined by backend */
        setnewpw_current_pw: currentPassword,
        setnewpw_pw: newPassword,
        setnewpw_pw_2: newPassword2
        /* eslint-enable @typescript-eslint/naming-convention */
      });
      closeDialog();
      openToast({
        title: "Password changed successfully"
      });
    } catch (error_0) {
      handleErrorWithToast({
        id: `setNewPassword-${Date.now().toString()}`,
        title: "Could not change password. Please try again",
        error: error_0
      });

      // Attempt to determine if backend error was due to an incorrect password.
      // If incorrect then set the error state for the input.
      const errorMarkup = getErrorDisplayMarkup(error_0);
      if (errorMarkup.includes(INCORRECT_PASSWORD_MSG)) {
        setIsCurrentPasswordError(true);
      }
    }
    setIsLoading(false);
  }
  function closeDialog() {
    setIsDialogOpen(false);
  }
  function onConfirm() {
    if (isResetPassword) {
      isResetSuccess ? void logout() : void resetPassword();
    } else {
      void changePassword();
    }
  }
  return <Grid container sx={{
    width: "100%",
    ...withEllipsis,
    alignItems: "center",
    justifyContent: "space-between"
  }}>
      <var>●●●●●●●●●●●●●●</var>

      {/* Button to open the dialog */}
      <FaroTextButton sx={{
      fontSize: DEFAULT_INPUT_FONT_SIZE,
      fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT
    }} onClick={onOpen}>
        Change
      </FaroTextButton>

      <Dialog title="Password" open={isDialogOpen} confirmText={confirmText} showSpinner={isLoading} isConfirmDisabled={isResetPassword ? false : !isValidForm} onConfirm={onConfirm} onCancel={closeDialog}>
        {/* Change password */}
        {!isResetPassword && <Grid sx={{
        width: "400px",
        maxWidth: "100%"
      }}>
            {/* Current password */}
            <Stack sx={{
          mb: SPACE_ELEMENTS_OF_MODAL
        }}>
              <SphereLabel title="Current password" isRequired rightSideComponent={email ? <Link component="button" onClick={onForgotPassword}>
                      Forgot password?
                    </Link> : undefined} />
              <TextField text={currentPassword} type={isCurrentPasswordVisible ? "text" : "password"} onTextChanged={setCurrentPassword} autoFocus={true} placeholder="Enter password" error={isCurrentPasswordError ? ErrorMessages.currentPassword : undefined} endAdornment={<PasswordInputButton isVisible={isCurrentPasswordVisible} onClick={() => setIsCurrentPasswordVisible(!isCurrentPasswordVisible)} />} />
            </Stack>

            {/* New password */}
            <Stack sx={{
          mb: "12px"
        }}>
              <SphereLabel title="New password" isRequired />
              <TextField text={newPassword} type={isNewPasswordVisible ? "text" : "password"} onTextChanged={setNewPassword} autoFocus={false} placeholder="Enter new password"
          // `true` rather than a message: the validation checker below the field already
          // says what is wrong, so this only has to turn the field red.
          error={!isPasswordValid && !!newPassword.length || undefined} endAdornment={<PasswordInputButton isVisible={isNewPasswordVisible} onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)} />} />

              <Stack sx={{
            marginTop: "12px"
          }}>
                <PasswordValidationChecker password={newPassword} onValidationChange={setIsPasswordValid} />
              </Stack>
            </Stack>

            {/* Repeat new password */}
            <Stack>
              <SphereLabel title="Repeat new password" isRequired />
              <TextField text={newPassword2} type={isNewPassword2Visible ? "text" : "password"} onTextChanged={setNewPassword2} autoFocus={false} placeholder="Re-enter new password" error={isNewPassword2Error ? ErrorMessages.newPassword2 : undefined} endAdornment={<PasswordInputButton isVisible={isNewPassword2Visible} onClick={() => setIsNewPassword2Visible(!isNewPassword2Visible)} />} />
            </Stack>
          </Grid>}

        {/* Reset password */}
        {isResetPassword && !isResetSuccess && <Grid sx={{
        width: "400px",
        maxWidth: "100%",
        mt: SPACE_ELEMENTS_OF_MODAL
      }}>
            <Stack sx={{
          mb: SPACE_ELEMENTS_OF_MODAL
        }}>
              To reset your password we will send you an email with a reset
              link.
            </Stack>
            <Stack>
              After clicking on the "Reset password" button you will be required
              to sign out from the application.
            </Stack>
          </Grid>}

        {/* Sign out after password reset */}
        {isResetPassword && isResetSuccess && <Grid sx={{
        width: "400px",
        maxWidth: "100%",
        mt: SPACE_ELEMENTS_OF_MODAL
      }}>
            <Stack sx={{
          mb: SPACE_ELEMENTS_OF_MODAL
        }}>
              Check your email inbox and follow the steps to reset your
              password.
            </Stack>
            <Stack>Click on the button to sign out.</Stack>
          </Grid>}
      </Dialog>
    </Grid>;
}